import { snackbar } from '@/_core/app-components/use';
import { useSendCommand } from '@/_core/cqrs/use';
import { RealTimeAction } from '@/_core/fluxstore/types';
import { createMoveCardToTaskCommand } from '../../../api/commands/factory/createMoveCard';
import { StorymapState } from '../../types';
import { getCardById, isActivity, isSubtask, isTask } from '../../use';
import { createTaskFromActivity, createTaskFromSubtask } from '../../use/mapCards';
import { useStorymapStore } from '../../useModule';
import { SetActiveCardIdByClick } from '../cardSelection/setActiveCardIdByClick';
import { FlashJustDragged } from './flashJustDragged';
import { HandleMoveCardResult } from './handleMoveCardResult';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class MoveCardToTask extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'MoveCardToTask';

    public constructor(public id: string, public activityId: string, public newOrder: number) {
        super(MoveCardToTask.ACTION_TYPE);
    }
}

const canMoveIntoMiddleware = async (state: StorymapState, action: MoveCardToTask): Promise<boolean> => {
    const card = getCardById(action.id);

    if (isActivity(card)) {
        const hasChild = state.tasks.some((x) => x.activityId === card.id);

        if (hasChild) {
            snackbar.showWarning("Can't move there because it has child cards");
            return false;
        }
    }

    return true;
};

const middleware = async (state: StorymapState, action: MoveCardToTask): Promise<boolean> => {
    (async () => {
        const cmd = await createMoveCardToTaskCommand(action);
        useSendCommand(action, {
            createCommand: () => cmd,
            onSuccess: (res) => {
                if (res.PushedActivity != null || res.PushedTask != null || (res.UnlinkedCardIds != null && res.UnlinkedCardIds.length > 0)) {
                    dispatch(new HandleMoveCardResult(res));
                }
            },
        });
    })();

    return true;
};

const reducer = (state: StorymapState, action: MoveCardToTask): void => {
    const card = getCardById(action.id);
    const destinationActivity = state.activities.find((x) => x.id == action.activityId);

    if (isTask(card)) {
        state.tasks.filter((x) => x.activityId === card.activityId && x.order > card.order).forEach((x) => x.order--);

        state.tasks.filter((x) => x.activityId === action.activityId && x.order >= action.newOrder).forEach((x) => x.order++);
        state.subtasks.filter((x) => x.taskId === card.id).forEach((x) => (x.activityId = action.activityId));

        card.lastUpdatedAt = new Date();
        card.order = action.newOrder;
        card.activityId = action.activityId;
    }

    if (isActivity(card)) {
        state.activities.filter((x) => x.order > card.order).forEach((x) => x.order--);
        state.activities = state.activities.filter((x) => x.id !== card.id);

        state.tasks.filter((x) => x.activityId === action.activityId && x.order >= action.newOrder).forEach((x) => x.order++);

        const task = createTaskFromActivity(card, destinationActivity, action.newOrder, state.activityColor, state.taskColor);
        task.lastUpdatedAt = new Date();
        state.tasks.push(task);
    }

    if (isSubtask(card)) {
        state.subtasks
            .filter((x) => x.taskId == card.taskId && x.releaseId == card.releaseId && x.order > card.order)
            .forEach((x) => x.order--);
        state.subtasks = state.subtasks.filter((x) => x.id !== card.id);

        state.tasks.filter((x) => x.activityId === action.activityId && x.order >= action.newOrder).forEach((x) => x.order++);

        const task = createTaskFromSubtask(card, destinationActivity, action.newOrder, state.subtaskColor, state.taskColor);
        task.lastUpdatedAt = new Date();
        state.tasks.push(task);
    }

    if (!action.isDispatchedByRealTimeCallback) {
        dispatch(new SetActiveCardIdByClick(card.id));
        dispatch(new FlashJustDragged());
    }
};

//@ts-ignore
registerMiddleware(MoveCardToTask.ACTION_TYPE, canMoveIntoMiddleware);

//@ts-ignore
registerMiddleware(MoveCardToTask.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(MoveCardToTask.ACTION_TYPE, reducer);
